import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Plus, Trash, PaperPlaneTilt, LinkSimple, Buildings } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CompanySwitcher } from "../components/CompanySwitcher";
import { BillingSection } from "../components/BillingSection";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";

export default function TeamPage() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "employee", department_id: "none" });
  const [deptName, setDeptName] = useState("");
  const [companyDialog, setCompanyDialog] = useState(false);
  const [company, setCompany] = useState({ name: "", currency: "USD", plan: "teams" });
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(() => {
    api.get("/workspaces/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/invitations").then((r) => setInvites(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateMember = async (memberId, patch) => {
    try {
      await api.put(`/workspaces/members/${memberId}`, patch);
      toast.success(t("toast_roleUpdated"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const sendInvite = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/invitations", {
        email: inviteForm.email,
        role: inviteForm.role,
        department_id: inviteForm.department_id === "none" ? "" : inviteForm.department_id,
        lang,
      });
      if (res.email_sent) {
        toast.success(t("toast_inviteSent"));
      } else {
        try { await navigator.clipboard.writeText(res.invite_link); } catch { /* clipboard may be blocked */ }
        toast.info(t("toast_inviteLink"));
      }
      setInviteForm({ email: "", role: "employee", department_id: "none" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const copyInviteLink = (inv) => {
    navigator.clipboard.writeText(`${window.location.origin}/join?token=${inv.token}`);
    toast.success(t("toast_linkCopied"));
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/invitations/${id}`);
      toast.success(t("toast_inviteRevoked"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addDept = async () => {
    try {
      await api.post("/departments", { name: deptName });
      setDeptName("");
      toast.success(t("toast_deptCreated"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const delDept = async (id) => {
    try {
      await api.delete(`/departments/${id}`);
      toast.success(t("toast_deptDeleted"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createCompany = async () => {
    setBusy(true);
    try {
      await api.post("/workspaces", { name: company.name, type: "team", currency: company.currency, plan: company.plan });
      toast.success(t("toast_companyCreated"));
      window.location.reload();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(data.workspace.invite_code);
    toast.success(t("toast_inviteCopied"));
  };

  if (!data) return null;
  const departments = data.departments || [];
  const depMap = Object.fromEntries(departments.map((d) => [d.id, d.name]));

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="company-page-title">{data.workspace.name}</h1>
          <p className="text-sm text-slate-500">
            {t("company_sub")} · <span className="font-mono uppercase text-[11px] border border-slate-300 px-1.5 py-0.5" data-testid="company-plan-badge">{t(`plan_${data.workspace.plan || "teams"}`)}</span>
          </p>
        </div>
        {isAdmin && (
          <Button data-testid="new-company-btn" onClick={() => setCompanyDialog(true)} variant="outline" className="rounded-none border-slate-300">
            <Buildings size={16} className="mr-1.5" /> {t("newCompany")}
          </Button>
        )}
      </div>

      <CompanySwitcher />

      <BillingSection isAdmin={isAdmin} />

      {data.workspace.invite_code && (
        <div className="border border-slate-200 p-5 flex items-center justify-between gap-4 bg-slate-50">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{t("inviteCodeLabel")}</p>
            <p className="font-mono text-xl font-semibold text-slate-900 mt-1" data-testid="invite-code-display">{data.workspace.invite_code}</p>
            <p className="text-xs text-slate-400 mt-1">{t("inviteShare")}</p>
          </div>
          <button data-testid="copy-invite-btn" onClick={copyInvite} className="p-2 border border-slate-300 hover:bg-white transition-colors duration-150"><Copy size={18} /></button>
        </div>
      )}

      <div className="border border-slate-200 p-5 space-y-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{t("inviteByEmail")}</p>
        <div className="flex flex-wrap gap-2">
          <Input data-testid="invite-email-input" type="email" placeholder={t("inviteEmailPh")} value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} className="rounded-none h-10 flex-1 min-w-[200px]" />
          <Select value={inviteForm.role} onValueChange={(v) => setInviteForm((f) => ({ ...f, role: v }))}>
            <SelectTrigger data-testid="invite-role-select" className="rounded-none h-10 w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employee">{t("role_employee")}</SelectItem>
              {isAdmin && <SelectItem value="manager">{t("role_manager")}</SelectItem>}
              {isAdmin && <SelectItem value="admin">{t("role_admin")}</SelectItem>}
            </SelectContent>
          </Select>
          <Select value={inviteForm.department_id} onValueChange={(v) => setInviteForm((f) => ({ ...f, department_id: v }))}>
            <SelectTrigger data-testid="invite-dept-select" className="rounded-none h-10 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("noDeptOption")}</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="send-invite-btn" onClick={sendInvite} disabled={busy || !inviteForm.email.trim()} className="rounded-none h-10 bg-slate-900 hover:bg-slate-700">
            <PaperPlaneTilt size={15} className="mr-1.5" /> {t("sendInvite")}
          </Button>
        </div>

        {invites.length > 0 && (
          <div className="border border-slate-200 divide-y divide-slate-100" data-testid="pending-invites-list">
            <p className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 bg-slate-50">{t("pendingInvites")}</p>
            {invites.map((inv) => (
              <div key={inv.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <span className="font-medium text-slate-800">{inv.email}</span>
                  <span className="text-xs text-slate-400 ml-2">{t(`role_${inv.role}`)}{inv.department_id && depMap[inv.department_id] ? ` · ${depMap[inv.department_id]}` : ""}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`copy-invite-link-${inv.email}`} onClick={() => copyInviteLink(inv)} title={t("copyLink")} className="p-1.5 text-slate-400 hover:text-slate-900 transition-colors duration-150"><LinkSimple size={15} /></button>
                  <button data-testid={`revoke-invite-${inv.email}`} onClick={() => revoke(inv.id)} title={t("revoke")} className="p-1.5 text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="border border-slate-200 p-5 space-y-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{t("departments")}</p>
          <div className="flex gap-2">
            <Input data-testid="dept-name-input" placeholder={t("newDepartmentPh")} value={deptName} onChange={(e) => setDeptName(e.target.value)} className="rounded-none h-10 max-w-xs" />
            <Button data-testid="add-dept-btn" onClick={addDept} disabled={!deptName.trim()} variant="outline" className="rounded-none h-10 border-slate-300">
              <Plus size={15} className="mr-1" /> {t("addDept")}
            </Button>
          </div>
          {departments.length === 0 ? (
            <p className="text-xs text-slate-400">{t("noDepartments")}</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="departments-list">
              {departments.map((d) => (
                <span key={d.id} className="inline-flex items-center gap-2 border border-slate-300 px-2.5 py-1 text-sm text-slate-700">
                  {d.name}
                  <button data-testid={`delete-dept-${d.name}`} onClick={() => delDept(d.id)} className="text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash size={13} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {[t("th_member"), t("th_email"), t("th_role"), t("th_department")].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="members-table-body">
            {data.members.map((m) => (
              <tr key={m.id} className="border-b border-slate-100">
                <td className="px-3 py-2.5 font-medium text-slate-900">{m.name}{m.id === user.id ? ` ${t("you")}` : ""}</td>
                <td className="px-3 py-2.5 text-slate-500">{m.email}</td>
                <td className="px-3 py-2.5">
                  {isAdmin && m.id !== user.id ? (
                    <Select value={m.role} onValueChange={(v) => updateMember(m.id, { role: v })}>
                      <SelectTrigger data-testid={`role-select-${m.email}`} className="rounded-none w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">{t("role_employee")}</SelectItem>
                        <SelectItem value="manager">{t("role_manager")}</SelectItem>
                        <SelectItem value="admin">{t("role_admin")}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="capitalize text-slate-600">{t(`role_${m.role}`)}</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <Select value={m.department_id || "none"} onValueChange={(v) => updateMember(m.id, { department_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid={`dept-select-${m.email}`} className="rounded-none w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("noDeptOption")}</SelectItem>
                      {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={companyDialog} onOpenChange={setCompanyDialog}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("newCompanyTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="new-company-name-input" placeholder={t("companyName")} value={company.name} onChange={(e) => setCompany((c) => ({ ...c, name: e.target.value }))} className="rounded-none h-11" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="new-company-currency-input" placeholder="USD" maxLength={3} value={company.currency} onChange={(e) => setCompany((c) => ({ ...c, currency: e.target.value.toUpperCase() }))} className="rounded-none h-11 font-mono uppercase" />
              <Select value={company.plan} onValueChange={(v) => setCompany((c) => ({ ...c, plan: v }))}>
                <SelectTrigger data-testid="new-company-plan-select" className="rounded-none h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="teams">{t("plan_teams")}</SelectItem>
                  <SelectItem value="business">{t("plan_business")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="create-company-btn" onClick={createCompany} disabled={busy || !company.name.trim()} className="rounded-none w-full h-11 bg-slate-900 hover:bg-slate-700">{t("createCompany")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
